import React, { useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { UserLanguage } from '../api';
import { Point, Size } from './digitalMapGeometry';
import { MapCameraOptions, useMapCamera } from './useMapCamera';
import { useMaximizedMap } from './useMaximizedMap';
import { GameDialog } from './GameDialog';
import '../styles/DigitalMapDisplay.css';
import { MapTopInsetContext } from './MapTopInsetContext';
import { observeElementResize } from './observeElementResize';

export function useMapViewport(gameId: string, options: MapCameraOptions, presentation: 'camera' | 'document' = 'camera') {
  const topInset = useContext(MapTopInsetContext);
  const maximized = useMaximizedMap(`${gameId}:${presentation}`);
  const controller = useMapCamera({ ...options, enabled: presentation === 'camera', topInset: maximized.maximized ? 0 : topInset });
  const { viewportRef } = controller;
  const [documentScale, setDocumentScale] = useState(0);
  useLayoutEffect(() => {
    if (presentation !== 'document' || !viewportRef.current) return;
    const measure = () => setDocumentScale((viewportRef.current?.getBoundingClientRect().width ?? 0) / options.board.width);
    measure();
    const disconnect = observeElementResize(viewportRef.current, measure);
    window.addEventListener('resize', measure);
    return () => { disconnect(); window.removeEventListener('resize', measure); };
  }, [presentation, options.board.width, viewportRef]);
  const scrollToPoint = useCallback((point: Point) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const bounds = viewport.getBoundingClientRect();
    window.scrollBy({ top: bounds.top + point.y * documentScale - window.innerHeight / 2, behavior: 'auto' });
  }, [documentScale, viewportRef]);
  return { ...controller, ...maximized, ...(presentation === 'document' ? {
    camera: { x: 0, y: 0, scale: documentScale }, ready: documentScale > 0, centerOn: scrollToPoint,
  } : {}) };
}

export type InvestigatorFocusRequest = { id: number; investigatorName: string };

type Props = {
  focusRequest?: InvestigatorFocusRequest;
  focusTarget?: Point;
  controller: ReturnType<typeof useMapViewport>;
  board: Size;
  target?: Point & { name: string };
  language?: UserLanguage;
  compact?: boolean;
  presentation?: 'camera' | 'document';
  variant: 'classic' | 'digital';
  children: React.ReactNode;
  inspectors?: React.ReactNode;
};

/** One camera and control surface for either board renderer. Inspectors stay outside the transform. */
export function MapViewport({ controller, board, target, focusRequest, focusTarget, language = 'En', compact = false, presentation = 'camera', variant, children, inspectors }: Props) {
  const id = useId();
  const [helpOpen, setHelpOpen] = useState(false);
  const russian = language === 'Ru';
  const { viewportRef, camera, ready, command, centerOn, stopMotion, mapRef, toggleRef, maximized, placeholderHeight, toggleMaximized } = controller;
  const lastFocusRequest = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!ready || !focusRequest || !focusTarget || lastFocusRequest.current === focusRequest.id) return;
    lastFocusRequest.current = focusRequest.id;
    centerOn(focusTarget, true);
    if (presentation === 'camera') viewportRef.current?.scrollIntoView({ block: 'nearest' });
    viewportRef.current?.focus({ preventScroll: true });
  }, [ready, focusRequest, focusTarget, centerOn, viewportRef, presentation]);
  if (presentation === 'document') return <div className={`map-camera-display ${variant}-map-display digital-map-document`} data-testid={`${variant}-map-display`}>
    <div ref={viewportRef} className="digital-map-document-viewport" role="region" tabIndex={0}
      aria-label={russian ? 'Цифровая карта' : 'Digital map'} style={{ height: board.height * camera.scale }}>
      <div className="digital-map-camera" style={{ width: board.width, height: board.height, opacity: ready ? 1 : 0,
        transform: `scale(${camera.scale})` }}>{children}</div>
    </div>
    {inspectors}
  </div>;
  const maximizeLabel = russian ? (maximized ? 'Свернуть карту' : 'Развернуть карту') : (maximized ? 'Restore map' : 'Maximize map');
  const instructions = russian
    ? 'Нажмите на локацию для просмотра. Перетаскивание и прокрутка перемещают карту; Ctrl+прокрутка или жест двумя пальцами меняют масштаб. Стрелки перемещают карту, +/− меняют масштаб, 0 подгоняет карту по ширине.'
    : 'Click a location to inspect. Drag or scroll to pan; Ctrl+scroll or pinch to zoom. Arrow keys pan, +/− zoom, 0 fits the width.';
  return <div className="digital-map-shell" style={{ height: placeholderHeight }}>
    <div ref={mapRef} className={`map-camera-display ${variant}-map-display${compact ? ' digital-map-compact' : ''}${maximized ? ' digital-map-maximized' : ''}`}
      role={maximized ? 'dialog' : undefined} aria-modal={maximized ? true : undefined}
      aria-label={maximized ? (russian ? 'Карта на весь экран' : 'Maximized map') : undefined} data-testid={`${variant}-map-display`}>
      <div className="digital-map-canvas">
        <div ref={viewportRef} className="digital-map-viewport" role="region" tabIndex={0}
          aria-label={russian ? (variant === 'classic' ? 'Классическая карта' : 'Цифровая карта') : (variant === 'classic' ? 'Classic map' : 'Digital map')}
          aria-describedby={`${id}-instructions`} data-scale={camera.scale} data-camera-x={camera.x} data-camera-y={camera.y}
          onClickCapture={stopMotion}>
          <div className="digital-map-camera" style={{ width: board.width, height: board.height, opacity: ready ? 1 : 0,
            transform: `translate(${camera.x}px, ${camera.y}px) scale(${camera.scale})` }}>{children}</div>
        </div>
        <div className="digital-map-controls" role="group" aria-label={russian ? 'Масштаб карты' : 'Map zoom'}>
          <button ref={toggleRef} type="button" className="digital-map-maximize" aria-label={maximizeLabel} title={maximizeLabel}
            aria-pressed={maximized} onClick={() => { stopMotion(); toggleMaximized(); }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <path d={maximized ? 'M3 9h6V3m6 0v6h6M3 15h6v6m6 0v-6h6' : 'M9 3H3v6m12-6h6v6M3 15v6h6m6 0h6v-6'} />
            </svg>
          </button>
          <button type="button" className="digital-map-find-investigator" disabled={!target}
            aria-label={russian ? 'Найти сыщика' : 'Find investigator'} title={target ? `Find ${target.name}` : 'No investigator on the map'}
            onClick={() => { if (target) centerOn(target); }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
              <circle cx="12" cy="12" r="7" /><circle cx="12" cy="12" r="2" /><path d="M12 1v5m0 12v5M1 12h5m12 0h5" />
            </svg>
          </button>
          <button type="button" className="digital-map-secondary-control" onClick={() => command('out')} aria-label={russian ? 'Уменьшить' : 'Zoom out'}>−</button>
          <button type="button" className="digital-map-secondary-control" onClick={() => command('fit')}>{russian ? 'По ширине' : 'Fit width'}</button>
          <button type="button" className="digital-map-secondary-control" onClick={() => command('in')} aria-label={russian ? 'Увеличить' : 'Zoom in'}>+</button>
          {compact && <button type="button" className="digital-map-secondary-control" aria-label="Map help" onClick={() => setHelpOpen(true)}>?</button>}
        </div>
      </div>
      <p hidden={compact} id={`${id}-instructions`} className="digital-map-instructions">{instructions}</p>
      {compact && helpOpen && <GameDialog title={russian ? 'Помощь по карте' : 'Map help'} onClose={() => setHelpOpen(false)}><p>{instructions}</p></GameDialog>}
      {inspectors}
    </div>
  </div>;
}
